import {
  formatCents,
  hasLiveFiskalDocument,
  parseMoneyToCents,
  sumCents,
  tt,
  type MoneyInput,
} from '@agos/shared'

/**
 * How much cash a waiter hands over at the end of their shift.
 *
 * Everything they took, less everything that never became cash in their apron:
 * the card payments the customer made directly, the vouchers they accepted, the
 * things they bought for the restaurant out of the till, and anything they paid
 * out. What is left is what goes in the safe.
 *
 * Ported from legacy `components/waiter-handover-modal.tsx`, in integer cents
 * (§14.7). This number is counted against physical banknotes, so it is the one
 * figure on the screen that has to be exactly right.
 */

export interface WaiterWalletSummary {
  totalRevenue?: MoneyInput
  orderCount?: number
  cashTotal?: MoneyInput
  cardTotal?: MoneyInput
  cardTerminalTotal?: MoneyInput
  cardCourierTotal?: MoneyInput
  cardOnlineTotal?: MoneyInput
  /**
   * A QR payment taken at the door - §10A.390, owner O112. The fifth bucket the summary answers since
   * then: before it a `qr_pay` order was in `totalRevenue` and in none of the four.
   */
  qrPayTotal?: MoneyInput
}

/**
 * One order as the wallet lists it - the two fields this file reads of it.
 *
 * `GET /employees/{id}/waiter-wallet` has answered the whole `orders` list since it was written and this
 * screen read only `summary`. The status is the field that makes the total legible.
 */
export interface WalletOrder {
  status?: string | null
  total?: MoneyInput
  /** §9.568 - `WalletService` answers both on every wallet read since §10A.293. */
  fiskalPrintedAt?: string | null
  fiskalStornoAt?: string | null
  /** §9.571 - so the registered takings can be split the way the drawer is counted. */
  paymentMethod?: string | null
}

/**
 * The statuses at which a table has actually paid - `FRONTEND.md §9.466`.
 *
 * `WalletService.COUNTED` is `status NOT IN ('cancelled', 'pending')`, so an order that is `new`,
 * `preparing`, `ready` or `quality_check` is counted as this waiter's takings **before anybody has paid
 * for it**. That is deliberate and its comment says why - *"a waitress mid-shift saw less than her
 * manager did and the two argued about the difference at handover"* - but it means the expected cash
 * includes money nobody has handed over yet.
 *
 * `delivered` is what the till writes on a close (`split-payment.ts`); `completed` is kept because
 * legacy wrote it and 0 rows carry it today, and a status this list forgets would be reported as unpaid.
 */
const PAID_STATUSES = new Set(['delivered', 'completed'])

/** What the shift is still owed, and on how many orders - §9.466. */
export interface UnpaidInWallet {
  cents: number
  orders: number
}

/**
 * How much of the expected cash sits on a table that has not paid - §9.466.
 *
 * **This is the figure the owner's 19,80 € question was about.** They asked why a waitress's handover was
 * short, and `docs/INVESTIGATION-19-80.md` names this as the leading explanation: a split writes a
 * `delivered` portion and **leaves the unpaid remainder open on the original**, which `COUNTED` counts -
 * so handing over before the table finally pays makes her expected cash include money nobody gave her,
 * by exactly the remainder. Their own follow-up was *"Sooo, its issue with rozdelenie stolov"*.
 *
 * **Said rather than subtracted**, and that distinction is the whole design. Excluding these orders
 * would change what a shift owes, which is a decision about how a restaurant runs and belongs to the
 * owner (`TODO.md` #18 carries it costed). Naming them changes nothing about the money and turns an
 * unexplainable shortfall into a visible one - which is §9.449's lesson from four hours earlier, where a
 * bare *Celková tržba* label invited a comparison between two different populations.
 */
export function unpaidInWallet(orders: readonly WalletOrder[]): UnpaidInWallet {
  const open = orders.filter((order) => !PAID_STATUSES.has(String(order.status ?? '')))
  return { cents: sumCents(...open.map((order) => order.total ?? 0)), orders: open.length }
}

/**
 * The sentence drawn beside the takings, or empty when every table has paid - §9.466.
 *
 * Empty on the ordinary shift, because a note saying *0,00 € is unpaid* on every handover is a note
 * nobody reads by the third day - and this one has to be read on the day it appears.
 */
export function unpaidNote(unpaid: UnpaidInWallet): string {
  if (unpaid.cents <= 0) return ''
  // `tt` here rather than an injected translator: the lint rule wants the literals where the
  // translation memory can see them, and `joinLineNotes` below takes a formatter only because its
  // caller has one already.
  const orders = unpaid.orders === 1 ? tt('objednávke') : tt('objednávkach')
  return `${tt('Z toho')} ${formatCents(unpaid.cents)} ${tt('na')} ${String(unpaid.orders)} ${orders} ${tt('bez úhrady - tieto peniaze ešte nikto neodovzdal.')}`
}

/** One typed line: an amount and what it was for. */
export interface HandoverLine {
  id: string
  amount: string
  note: string
}

export function lineTotalOf(lines: HandoverLine[]): number {
  return sumCents(...lines.map((line) => line.amount))
}

export interface HandoverTotals {
  totalRevenueCents: number
  /**
   * The cash the wallet recorded - §9.456.
   *
   * Not part of any deduction: it is the kasa's own side of the comparison, which
   * `HandoverReconciliation` needs and this payload was not sending. Absent it stored as nothing and
   * read as nought, which is the *"Kasa zaznamenala na kartách 0,00 €"* the owner asked about.
   */
  cashCents: number
  /**
   * The *Hotovosť* row: the takings less every method that never became cash in the apron.
   *
   * By subtraction, as the dialog has always drawn it, so an order of a method no bucket names still
   * shows up as cash rather than vanishing. Moved here from the dialog by §9.741, which is what gave
   * it a test: the row had to learn QR Pay, and a subtraction written inline in JSX is one nobody runs.
   */
  cashTakingsCents: number
  cardOnlineCents: number
  cardTerminalCents: number
  cardCourierCents: number
  /**
   * A QR payment at the door - §9.741, owner O112. **Taken off automatically, like the online card**:
   * the bank took the money and the waiter never held it, which is `HandoverReconciliation.cashToHandOver`
   * since §10A.390. Deducted whether or not the dialog draws its row, so a nought costs nothing.
   */
  qrPayCents: number
  voucherCents: number
  purchaseCents: number
  payoutCents: number
  deductionCents: number
  cashToHandOverCents: number
}

export function handoverTotals({
  summary,
  vouchers,
  purchases,
  payouts,
}: {
  summary: WaiterWalletSummary | null
  vouchers: HandoverLine[]
  purchases: HandoverLine[]
  payouts: HandoverLine[]
}): HandoverTotals {
  // The three card fields are deducted individually, never `cardTotal` — that
  // field is their sum, and subtracting both would take the card money off
  // twice.
  const cardOnline = parseMoneyToCents(summary?.cardOnlineTotal)
  const cardTerminal = parseMoneyToCents(summary?.cardTerminalTotal)
  const cardCourier = parseMoneyToCents(summary?.cardCourierTotal)
  // §9.741 - beside the online card and for the same reason: it reached the bank, not the waiter.
  const qrPay = parseMoneyToCents(summary?.qrPayTotal)
  // §9.456 - carried through untouched, for the payload rather than for the arithmetic.
  const cash = parseMoneyToCents(summary?.cashTotal)

  const voucher = lineTotalOf(vouchers)
  const purchase = lineTotalOf(purchases)
  const payout = lineTotalOf(payouts)

  const totalRevenue = parseMoneyToCents(summary?.totalRevenue)
  const deduction = cardOnline + qrPay + cardTerminal + cardCourier + voucher + purchase + payout

  return {
    totalRevenueCents: totalRevenue,
    cashCents: cash,
    cashTakingsCents: totalRevenue - cardOnline - qrPay - cardTerminal - cardCourier,
    cardOnlineCents: cardOnline,
    cardTerminalCents: cardTerminal,
    cardCourierCents: cardCourier,
    qrPayCents: qrPay,
    voucherCents: voucher,
    purchaseCents: purchase,
    payoutCents: payout,
    deductionCents: deduction,
    // Deliberately not clamped at zero. A waiter who paid out more than they
    // took is owed money, and a figure that silently floored at zero would hide
    // that from the person counting the safe.
    cashToHandOverCents: totalRevenue - deduction,
  }
}

/**
 * The backend stores two fixed payout slots and two purchase slots, not a list.
 * Several typed lines collapse into the first slot with their notes joined, so
 * the detail survives as text even though the schema has nowhere structured to
 * put it.
 */
export function joinLineNotes(lines: HandoverLine[], formatAmount: (cents: number) => string): string {
  return lines
    .filter((line) => parseMoneyToCents(line.amount) > 0)
    .map((line) => {
      const amount = formatAmount(parseMoneyToCents(line.amount))
      return line.note ? `${line.note} (${amount})` : amount
    })
    .join(' + ')
}

/** How much of a handover the register actually has a document for - §9.568. */
export interface DocumentedInWallet {
  cents: number
  orders: number
}

/**
 * The part of these takings the register registered - §9.568, FB-863.
 *
 * **The owner's ask**: *"add info like - price overall and price only with fiskal doklady, so when
 * they do uzávierka, they will be correct."* `FiskalClosureService.takingsSince` - the drawer a
 * closure is counted against - counts only orders whose document is printed and not cancelled, and
 * this figure is the same subset of one person's takings.
 *
 * **It does not make the two figures equal, and the note says so.** §9.449 measured the other reason
 * they differ: a handover is one employee's takings and a *záverečka* is one device's, whoever rang it
 * up - a 105,00 € gap on their own restaurant data. What this answers is *how much of what I am
 * handing over does the register know about*, which was not on the screen at all.
 */
export function documentedInWallet(orders: readonly WalletOrder[]): DocumentedInWallet {
  const registered = orders.filter((order) => hasLiveFiskalDocument(order))
  return {
    cents: sumCents(...registered.map((order) => order.total ?? 0)),
    orders: registered.length,
  }
}

/**
 * The sentence beside the takings, naming the registered part - §9.568.
 *
 * **Always shown, unlike `unpaidNote`.** That one is empty on an ordinary shift because a line reading
 * *0,00 € unpaid* on every handover stops being read; this one is the comparison the operator is being
 * asked to make, so its absence would be the absence of the answer. When the two figures agree it says
 * so, which is the useful reading on a clean shift.
 */
export function documentedNote(
  documented: DocumentedInWallet,
  totalCents: number,
  orderCount = 0,
): string {
  /* §9.577 - **the count, because the money alone cannot say which of two things is wrong.** The owner
   * read *S fiškálnym dokladom 0,00 z 382,90* on a shift whose receipts they had watched print, and
   * that sentence is true of two different situations: her orders carry no print stamp, or her orders
   * are not the printed ones. **`0 z 12` and `0 z 0` are different reports and the money is identical
   * in both.** `DocumentedInWallet` has carried this figure since §9.568 with nothing rendering it.
   *
   * And it is the fastest way to see the window difference: this wallet is orders **created** today
   * (`WalletService.EFFECTIVE_DATE` is `created_at`, or `scheduled_at` for a preorder) while the drawer
   * is receipts **printed** since the last closing - so an order created yesterday and printed today is
   * in one and not the other. §9.449 named *one employee against one device* and not this second axis. */
  const counted = orderCount > 0 ? ` (${documented.orders}/${orderCount} ${tt('objednávok')})` : ''
  if (documented.cents === totalCents) {
    return `${tt('Celá suma')} (${formatCents(totalCents)}) ${tt('má fiškálny doklad.')}${counted}`
  }
  const missing = totalCents - documented.cents
  return `${tt('S fiškálnym dokladom')} ${formatCents(documented.cents)}${counted} ${tt('z')} ${formatCents(totalCents)} - ${tt('bez dokladu')} ${formatCents(missing)}. ${tt('Uzávierka počíta len sumy s dokladom.')}`
}

/** The registered takings split the way the drawer is counted - §9.571. */
export interface DocumentedSplit {
  cashCents: number
  cardCents: number
  /** §9.577 - the payload does not carry the stamps, so neither figure means anything. */
  unknown: boolean
}

/**
 * *Fiskal Hotovosť* and *Fiskal Karta* - §9.571, owner instruction 2026-09-28.
 *
 * **Why the split rather than one figure.** `FiskalClosureService.drawerStatus` reports cash and card
 * apart and **adds only the cash**, because card takings are not in the drawer - its own javadoc says
 * so. So a single registered total cannot be compared against either half of what the closure shows,
 * and the person counting a drawer is holding the cash figure alone.
 *
 * **The method names come from the closure's own reading**: everything that is not `cash` is card
 * there, so the same rule is used here rather than a second list of card methods that could drift.
 *
 * **Behind `fiskal_reconcile`**, by the owner's instruction - the caller gates it, because a rule about
 * money should not also decide who may read it.
 */
export function documentedSplit(orders: readonly WalletOrder[]): DocumentedSplit {
  const registered = orders.filter((order) => hasLiveFiskalDocument(order))
  const cash = registered.filter((order) => (order.paymentMethod ?? '') === 'cash')
  const card = registered.filter((order) => (order.paymentMethod ?? '') !== 'cash')
  return {
    cashCents: sumCents(...cash.map((order) => order.total ?? 0)),
    cardCents: sumCents(...card.map((order) => order.total ?? 0)),
    unknown: documentsAreUnanswered(orders),
  }
}

/**
 * Whether this payload answers the question at all - §9.577, FB-879.
 *
 * **The owner watched receipts print and the screen said none of 382,90 EUR had a document.**
 * `hasLiveFiskalDocument` is `(order.fiskalPrintedAt ?? '') !== ''`, so **an absent field reads exactly
 * like an unprinted order** - and `WalletService` only began answering those two stamps on 2026-09-27
 * at 23:55 (§10A.293), one commit before §9.568 built the figure that reads them. So any server older
 * than that makes the split 0 / 0 for every order and the screen states it as fact.
 *
 * ***Absent is not false*, on a tax surface** - FB-837's shape, where a check that never ran reports
 * that it found nothing.
 *
 * **The key rather than the value is the signal.** A list where **not one** order carries
 * `fiskalPrintedAt` at all is a payload that cannot answer; a list where the key is present and null is
 * a real *nothing has printed*. **An empty list is neither**: that is the ordinary *nothing in this
 * wallet yet*, and calling it unknown would replace a correct nought with a shrug on every quiet shift.
 */
export function documentsAreUnanswered(orders: readonly WalletOrder[]): boolean {
  if (orders.length === 0) return false
  return !orders.some((order) => 'fiskalPrintedAt' in order)
}
